'use strict'

import * as PIXI from 'pixi.js'
import anime from 'animejs'

/**
 * Blur Particle Class
 *
 * @constructor
 * @param {DOMElement} el
 * @param {Object} options
 */
export class Particle {
  constructor(el, options = {}) {
    this.parentElement = el
    this.points = []
    this.app = false
    this.ratio = false
    this.containerLow = false

    this.sprites = []

    this.baseFillColor = 0xFFFFFF
    this.particleColors = options.colors || [
      '#FFF8E1',
      '#FFF8E1',
      '#FFECB3',
      '#FFECB3',
      '#FFE082',
      '#FFE082',
      '#FFD54F',
      '#FFCA28',
      '#FFC107',
      '#FFB300',
      '#FFA000',
      '#FF8F00',
      '#FF6F00',
      '#E65100',
      '#BF360C'
    ]
    this.particleColors2 = [
      '#E0F2F1',
      '#E0F2F1',
      '#B2DFDB',
      '#B2DFDB',
      '#80CBC4',
      '#80CBC4',
      '#4DB6AC',
      '#26A69A',
      '#009688',
      '#00897B',
      '#00796B',
      '#00695C',
      '#004D40'
    ]

    this.init()
  }

  init() {
    const { width: elWidth, height: elHeight } = this.parentElement.getBoundingClientRect()

    let width = 200
    let height = 200

    this.ratio = elWidth / elHeight

    if (elWidth / elHeight >= this.ratio) {
      width = height * this.ratio
    } else {
      height = width / this.ratio
    }

    this.app = new PIXI.Application(width, height, {
      transparent: true,
      resolution: 0.25,
      autoResize: false,
      antialias: false
    })
    this.parentElement.appendChild(this.app.view)

    this.w = width
    this.h = height
    this.count = Math.floor(Math.max(this.w, this.h) / 4)
    this.delta = 0.75
    this.ratio = width / height

    const particlesOptions = {
      scale: false,
      position: true,
      rotation: false,
      uvs: false,
      alpha: true
    }
    this.containerLow = new PIXI.particles.ParticleContainer(this.count, particlesOptions)
    this.app.stage.addChild(this.containerLow)

    const graphicsLevelLarge = new PIXI.Graphics()
    const graphicsSize = Math.max(this.w, this.h) / 2
    graphicsLevelLarge.beginFill(this.baseFillColor)
    graphicsLevelLarge.drawCircle(0, 0, graphicsSize)
    graphicsLevelLarge.endFill()
    graphicsLevelLarge.setTransform(0, 0, 0.7, 0.7)
    graphicsLevelLarge._filters = [new PIXI.filters.BlurFilter(graphicsSize / 8)]
    this.textureBlurLevelHigh = this.app.renderer.generateTexture(graphicsLevelLarge)

    for (let i = 0; i < this.count; i++) {
      this.initPoint(i)
    }

    for (let i = 0; i < this.points.length; i++) {
      const texture = this.textureBlurLevelHigh
      const s = new PIXI.Sprite(texture)
      s.width = this.points[i].size
      s.height = this.points[i].size
      s.position.x = this.points[i].x
      s.position.y = this.points[i].y
      s.anchor.x = 0.5
      s.anchor.y = 0.5
      s.alpha = this.points[i].alpha
      this.sprites.push(s)
      this.containerLow.addChild(s)
    }

    this.containerLow.x = this.app.screen.width / 2
    this.containerLow.y = this.app.screen.height / 2
    this.containerLow.pivot.x = this.app.screen.width / 2
    this.containerLow.pivot.y = this.app.screen.height / 2

    this.app.ticker.add(delta => {
      this.updatePosition()
    }, PIXI.UPDATE_PRIORITY.LOW)

    this.animateParticules()
  }

  changeColor(colors = []) {
    this.particleColors2 = [...colors]
  }

  renderParticule(anim) {
    for (let i = 0; i < anim.animatables.length; i++) {
      anim.animatables[i].target.draw(i)
    }
  }

  animateParticules() {
    if (this.tl) {
      this.tl.pause()
      this.tl = false
    }
    this.tl = anime({
      targets: this.points,
      tint: (p) => {
        const color = this.constructor.UtilRandomArray(this.particleColors2)
        return [
          p.tint,
          color
        ]
      },
      duration(el, i, l) {
        return 2500 + anime.random(0, 1000)
      },
      easing: 'easeInOutSine',
      complete: () => {
        this.animateParticules()
      },
      direction: 'alternate',
      update: this.renderParticule
    })
  }

  /**
   * ポイントの初期化
   */
  initPoint(index = 0) {
    const x = this.w * Math.random()
    const y = this.h * Math.random()
    const dx = -this.delta / 10 + this.delta * Math.random()
    const dy = -this.delta / 10 + this.delta * Math.random()

    const size = this.constructor.UtilRandom(Math.max(this.w, this.h) / 2, Math.max(this.w, this.h) / 2 * 2)
    const alpha = 0.5

    const p = {
      x: x,
      y: y,
      dx: dx,
      dy: dy,
      size: size,
      alpha: alpha,
      tint: this.constructor.UtilRandomArray(this.particleColors),
      draw: (i) => {
        this.sprites[i].tint = this.constructor.UtilRgba2hex(p.tint)
      }
    }

    this.points.push(p)
  }

  /**
   * ポイントの描画
   */
  drawPoint(point, i) {
    this.sprites[i].position.x = point.x
    this.sprites[i].position.y = point.y
    this.sprites[i].tint = this.constructor.UtilRgba2hex(point.tint)
  }

  /**
   * ポイントの位置変更
   */
  updatePosition() {
    for (let i = 0; i < this.points.length; i++) {
      // for Position Update
      let xDx = this.points[i].x + this.points[i].dx
      let yDy = this.points[i].y + this.points[i].dy

      if (xDx - this.points[i].size < -(this.w * 1.5) || xDx + this.points[i].size > (this.w * 1.5)) {
        this.points[i].dx = -this.points[i].dx
      }
      if (yDy - this.points[i].size < -(this.h * 1.5) || yDy + this.points[i].size > (this.h * 1.5)) {
        this.points[i].dy = -this.points[i].dy
      }

      this.points[i].x += this.points[i].dx
      this.points[i].y += this.points[i].dy

      this.drawPoint(this.points[i], i)
    }
  }

  destroy() {
    this.app.destroy(true)
    this.app = null
  }

  /**
   * ランダムに値を取得する
   *
   * @static
   * @param {Number} min
   * @param {Number} max
   * @return {Number}
   */
  static UtilRandom(min = 0, max = 10) {
    return Math.floor((Math.random() * (max - min + 1)) + min)
  }

  /**
   * 配列内からランダムに値を取得する
   *
   * @static
   * @param {Array} array 配列
   * @return {Object} 配列内の値
   */
  static UtilRandomArray(array) {
    return array[Math.floor(Math.random() * array.length)]
  }

  /**
   * 少数点を第n位を切り捨てる
   *
   * @static
   * @param {Number} number
   * @param {Number} n - 第n位
   * @return {Number}
   */
  static UtilMathPow(number = 0, n = 5) {
    return Math.floor(number * Math.pow(10, n)) / Math.pow(10, n)
  }

  /**
   * RGBA convert to HEX
   * https://jsfiddle.net/Mottie/xcqpF/316/
   * https://stackoverflow.com/questions/49974145/how-to-convert-rgba-to-hex-color-code-using-javascript
   *
   * @static
   * @param {String} rgba
   * @return {String} hex
   */
  static UtilRgba2hex(orig = 'rgba(0, 0, 0, 0)') {
    let rgb = orig.replace(/\s/g, '').match(/^rgba?\((\d+),(\d+),(\d+)/i)
    return (rgb && rgb.length === 4) ? '0x' +
      ('0' + parseInt(rgb[1], 10).toString(16)).slice(-2) +
      ('0' + parseInt(rgb[2], 10).toString(16)).slice(-2) +
      ('0' + parseInt(rgb[3], 10).toString(16)).slice(-2) : orig
  }
}
